import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  ApiResponse,
  AuthResponse,
  LoginRequest,
  User,
  Client,
  CreateClientRequest,
  UpdateClientRequest,
  Account,
  CreateAccountRequest,
  UpdateAccountRequest,
  AccountMatchingRule,
  TestMatchingRequest,
  TestMatchingResponse,
  CommissionPreset,
  CreatePresetRequest,
  DailyBlotter,
  Transaction,
  QueueStatus,
  BlotterStatistics,
  QueueStatistics,
  CreateAccountMatchingRuleRequest,
  UpdateAccountMatchingRuleRequest,
  PaginatedResponse
} from '../types/api';
import {
  CreateUserRequest,
  UpdateUserRequest,
  UserFilters
} from '../types/user';
import type {
  ManualCheck,
  ManualCheckListResponse,
  ManualCheckListFilters,
  CreateManualCheckPayload,
  UpdateManualCheckPayload,
  ManualCheckSalesmanDetail,
} from '../types/manualCheck';

// Storage keys - centralized for consistency
const AUTH_TOKEN_KEY = 'auth_token';
const AUTH_USER_KEY = 'auth_user';
const AUTH_TIMESTAMP_KEY = 'auth_timestamp';

class ApiService {
  private client: AxiosInstance;
  private token: string | null = null;
  private isLoggingIn: boolean = false;

  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        // Get fresh token from memory or storage
        const currentToken = this.token || this.getStoredToken();
        if (currentToken) {
          config.headers.Authorization = `Bearer ${currentToken}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle errors
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        const isLoginRequest = error.config?.url?.includes('/auth/login');
        const isLogoutRequest = error.config?.url?.includes('/auth/logout');
        const isProfileRequest = error.config?.url?.includes('/auth/me');

        // Handle 401 Unauthorized
        // Don't clear and redirect if we're in the middle of logging in
        // or if this is an auth-related request
        if (error.response?.status === 401 && !isLoginRequest && !isLogoutRequest && !isProfileRequest) {
          // Don't clear token during login flow
          if (!this.isLoggingIn) {
            this.clearAllAuthData();

            // Only redirect if not already on login page
            if (!window.location.pathname.includes('/login') && window.location.pathname !== '/') {
              window.location.href = '/';
            }
          }
        }

        // Handle 419 CSRF token mismatch (Laravel specific)
        if (error.response?.status === 419 && !this.isLoggingIn) {
          this.clearAllAuthData();
          window.location.href = '/';
        }

        return Promise.reject(error);
      }
    );

    // Initialize token from localStorage with validation
    this.initializeFromStorage();
  }

  /**
   * Get token from storage
   */
  private getStoredToken(): string | null {
    try {
      return localStorage.getItem(AUTH_TOKEN_KEY);
    } catch {
      return null;
    }
  }

  /**
   * Initialize auth state from storage with validation
   */
  private initializeFromStorage(): void {
    try {
      const savedToken = localStorage.getItem(AUTH_TOKEN_KEY);
      const timestamp = localStorage.getItem(AUTH_TIMESTAMP_KEY);

      if (savedToken) {
        // Check if token is not too old (optional: 30 days max)
        if (timestamp) {
          const tokenAge = Date.now() - parseInt(timestamp, 10);
          const maxAge = 30 * 24 * 60 * 60 * 1000; // 30 days

          if (tokenAge > maxAge) {
            // Token is too old, clear it
            this.clearAllAuthData();
            return;
          }
        }

        // Validate token format (basic JWT check)
        if (this.isValidTokenFormat(savedToken)) {
          this.token = savedToken;
        } else {
          // Invalid token format, clear it
          this.clearAllAuthData();
        }
      }
    } catch (error) {
      // Storage access failed, clear everything
      this.clearAllAuthData();
    }
  }

  /**
   * Basic token format validation
   * Supports both JWT and Laravel Sanctum plain tokens
   */
  private isValidTokenFormat(token: string): boolean {
    if (!token || typeof token !== 'string') return false;

    // Token should be a non-empty string with reasonable length
    // Laravel Sanctum tokens are typically 40+ characters
    // JWT tokens have 3 parts separated by dots
    return token.length >= 10;
  }

  /**
   * Set token with timestamp
   */
  setToken(token: string): void {
    if (!token || typeof token !== 'string' || token.length < 10) {
      console.warn('Attempted to set invalid token');
      return;
    }

    this.token = token;

    try {
      localStorage.setItem(AUTH_TOKEN_KEY, token);
      localStorage.setItem(AUTH_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error('Failed to save token to storage:', error);
    }
  }

  /**
   * Clear only token
   */
  clearToken(): void {
    this.token = null;
    try {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(AUTH_TIMESTAMP_KEY);
    } catch {
      // Ignore storage errors
    }
  }

  /**
   * Clear all authentication data - full cleanup
   */
  clearAllAuthData(): void {
    this.token = null;

    try {
      // Remove all auth-related items
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(AUTH_USER_KEY);
      localStorage.removeItem(AUTH_TIMESTAMP_KEY);
    } catch {
      // Ignore storage errors during cleanup
    }
  }

  getToken(): string | null {
    return this.token;
  }

  /**
   * Check if user is authenticated (has valid token)
   */
  isAuthenticated(): boolean {
    const token = this.token || this.getStoredToken();
    return token !== null && this.isValidTokenFormat(token);
  }

  // Authentication Methods
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    // Set flag to prevent 401 interceptor from clearing token during login
    this.isLoggingIn = true;

    try {
      // Clear any existing auth data before login attempt
      // This prevents stale token issues
      this.clearAllAuthData();

      const response: AxiosResponse<AuthResponse> = await this.client.post('/auth/login', credentials);

      if (response.data.token) {
        this.setToken(response.data.token);

        // Store user data for quick access
        if (response.data.user) {
          try {
            localStorage.setItem(AUTH_USER_KEY, JSON.stringify(response.data.user));
          } catch {
            // Ignore storage errors
          }
        }
      }

      return response.data;
    } finally {
      // Clear the login flag after a short delay to allow
      // initial authenticated requests to complete
      setTimeout(() => {
        this.isLoggingIn = false;
      }, 2000);
    }
  }

  async logout(): Promise<void> {
    try {
      // Attempt server-side logout
      await this.client.post('/auth/logout');
    } catch (error) {
      // Continue with client-side cleanup even if server logout fails
      console.warn('Server logout failed, clearing local auth data');
    } finally {
      // Always clear all auth data
      this.clearAllAuthData();
    }
  }

  /**
   * Validate current token with server
   */
  async validateToken(): Promise<boolean> {
    if (!this.isAuthenticated()) {
      return false;
    }

    try {
      await this.client.get('/auth/me');
      return true;
    } catch (error: any) {
      if (error.response?.status === 401) {
        this.clearAllAuthData();
        return false;
      }
      // Network error or other issue - don't clear token
      throw error;
    }
  }

  async getProfile(): Promise<ApiResponse<User>> {
    const response: AxiosResponse<ApiResponse<User>> = await this.client.get('/auth/me');
    return response.data;
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<ApiResponse<any>> {
    const response = await this.client.post('/auth/change-password', {
      old_password: oldPassword,
      new_password: newPassword,
      new_password_confirmation: newPassword,
    });
    return response.data;
  }

  // Dashboard Methods
  async getDashboardOverview(): Promise<ApiResponse<any>> {
    const response = await this.client.get('/dashboard/overview');
    return response.data;
  }

  async getTopPerformers(): Promise<ApiResponse<any>> {
    const response = await this.client.get('/dashboard/top-performers');
    return response.data;
  }

  async getTopAccounts(): Promise<ApiResponse<any>> {
    const response = await this.client.get('/dashboard/top-accounts');
    return response.data;
  }

  async getTopClients(): Promise<ApiResponse<any>> {
    const response = await this.client.get('/dashboard/top-clients');
    return response.data;
  }

  // Client Management Methods
  async getClients(params?: {
    page?: number;
    per_page?: number;
    search?: string;
    status?: 'active' | 'inactive';
    no_accounts?: boolean | number | string;
  }): Promise<PaginatedResponse<Client>> {
    const response = await this.client.get('/clients', { params });
    return response.data;
  }

  async getClient(id: number): Promise<ApiResponse<Client>> {
    const response = await this.client.get(`/clients/${id}`);
    return response.data;
  }

  async createClient(data: CreateClientRequest): Promise<ApiResponse<Client>> {
    const response = await this.client.post('/clients', data);
    return response.data;
  }

  async updateClient(id: number, data: UpdateClientRequest): Promise<ApiResponse<Client>> {
    const response = await this.client.put(`/clients/${id}`, data);
    return response.data;
  }

  async deleteClient(id: number, force = false): Promise<ApiResponse<any>> {
    const response = await this.client.delete(`/clients/${id}`, {
      params: force ? { force: true } : undefined
    });
    return response.data;
  }

  async toggleClientStatus(id: number): Promise<ApiResponse<Client>> {
    const response = await this.client.post(`/clients/${id}/toggle-status`);
    return response.data;
  }

  // Account Management Methods
  async getAccounts(params?: {
    page?: number;
    per_page?: number;
    search?: string;
    status?: 'active' | 'inactive';
    client_id?: number;
    user_id?: number;
    unassigned?: boolean | number | string;
    no_employees?: boolean | number | string;
  }): Promise<PaginatedResponse<Account>> {
    const response = await this.client.get('/accounts', { params });
    return response.data;
  }

  async getAccount(id: number): Promise<ApiResponse<Account>> {
    const response = await this.client.get(`/accounts/${id}`);
    return response.data;
  }

  async createAccount(data: CreateAccountRequest): Promise<ApiResponse<Account>> {
    const response = await this.client.post('/accounts', data);
    return response.data;
  }

  async updateAccount(id: number, data: UpdateAccountRequest): Promise<ApiResponse<Account>> {
    const response = await this.client.put(`/accounts/${id}`, data);
    return response.data;
  }

  async deleteAccount(id: number, force = false): Promise<ApiResponse<any>> {
    const response = await this.client.delete(`/accounts/${id}`, {
      params: force ? { force: true } : undefined
    });
    return response.data;
  }

  async toggleAccountStatus(id: number): Promise<ApiResponse<Account>> {
    const response = await this.client.post(`/accounts/${id}/toggle-status`);
    return response.data;
  }

  async getAvailableClients(): Promise<ApiResponse<Client[]>> {
    const response = await this.client.get('/accounts/available-clients');
    return response.data;
  }

  async getAccountMatches(accountId: number): Promise<ApiResponse<any[]>> {
    const response = await this.client.get(`/accounts/${accountId}/matches`);
    return response.data;
  }

  async bulkAssignEmployeesToAccounts(data: {
    account_ids: number[];
    employees: Array<{ user_id: number; commission_percentage: number }>;
    overwrite_existing: boolean;
  }): Promise<ApiResponse<any>> {
    const response = await this.client.post('/accounts/bulk-assign-employees', data);
    return response.data;
  }

  async bulkAssignEmployeesToClient(clientId: number, data: {
    employees: Array<{ user_id: number; commission_percentage: number }>;
    apply_to: 'all_accounts' | 'active_accounts_only';
    overwrite_existing: boolean;
  }): Promise<ApiResponse<any>> {
    const response = await this.client.post(`/clients/${clientId}/bulk-assign-employees`, data);
    return response.data;
  }

  // Account Matching Methods
  async getAccountMatchingRules(params?: {
    page?: number;
    per_page?: number;
  }): Promise<PaginatedResponse<AccountMatchingRule>> {
    const response = await this.client.get('/account-matching', { params });
    return response.data;
  }

  async createAccountMatchingRule(data: CreateAccountMatchingRuleRequest): Promise<ApiResponse<AccountMatchingRule>> {
    const response = await this.client.post('/account-matching', data);
    return response.data;
  }

  async updateAccountMatchingRule(
    id: number, 
    data: UpdateAccountMatchingRuleRequest
  ): Promise<ApiResponse<AccountMatchingRule>> {
    const response = await this.client.put(`/account-matching/${id}`, data);
    return response.data;
  }

  async deleteAccountMatchingRule(id: number): Promise<ApiResponse<any>> {
    const response = await this.client.delete(`/account-matching/${id}`);
    return response.data;
  }

  async testAccountMatching(data: TestMatchingRequest): Promise<ApiResponse<TestMatchingResponse>> {
    const response = await this.client.post('/account-matching/test', data);
    return response.data;
  }

  async getMatchingSuggestions(): Promise<ApiResponse<any[]>> {
    const response = await this.client.get('/account-matching/suggestions');
    return response.data;
  }

  async checkMatchingConflicts(data: CreateAccountMatchingRuleRequest): Promise<ApiResponse<any[]>> {
    const response = await this.client.post('/account-matching/check-conflicts', data);
    return response.data;
  }

  // Commission Methods
  async getCommissionPresets(params?: {
    page?: number;
    per_page?: number;
  }): Promise<PaginatedResponse<CommissionPreset>> {
    const response = await this.client.get('/commission/presets', { params });
    return response.data;
  }

  async createCommissionPreset(data: CreatePresetRequest): Promise<ApiResponse<CommissionPreset>> {
    const response = await this.client.post('/commission/presets', data);
    return response.data;
  }

  async updateCommissionPreset(
    id: number, 
    data: Partial<CreatePresetRequest>
  ): Promise<ApiResponse<CommissionPreset>> {
    const response = await this.client.put(`/commission/presets/${id}`, data);
    return response.data;
  }

  async deleteCommissionPreset(id: number): Promise<ApiResponse<any>> {
    const response = await this.client.delete(`/commission/presets/${id}`);
    return response.data;
  }

  async getPopularPresets(): Promise<ApiResponse<CommissionPreset[]>> {
    const response = await this.client.get('/commission/popular-presets');
    return response.data;
  }

  async calculateCommission(data: {
    account_string: string;
    security?: string;
    trade_type: string;
    bought: number;
    sold: number;
    price: number;
  }): Promise<ApiResponse<any>> {
    const response = await this.client.post('/commission/calculate', data);
    return response.data;
  }

  // Blotter Methods
  async getDailyBlotters(params?: {
    status?: 'pending' | 'processing' | 'completed' | 'error';
    date_from?: string;
    date_to?: string;
    page?: number;
    per_page?: number;
  }): Promise<PaginatedResponse<DailyBlotter>> {
    const response = await this.client.get('/blotter', { params });
    return response.data;
  }

  async getDailyBlotter(id: number): Promise<ApiResponse<DailyBlotter>> {
    const response = await this.client.get(`/blotter/${id}`);
    return response.data;
  }

  async uploadCsv(file: File, processImmediately: boolean = false): Promise<ApiResponse<DailyBlotter>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('process_immediately', processImmediately.toString());

    const response = await this.client.post('/blotter/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
        // You can emit this progress to a global state or callback
        console.log('Upload Progress:', percentCompleted + '%');
      },
    });
    return response.data;
  }

  async batchUploadCsv(
    files: File[], 
    processImmediately: boolean = false,
    highPriority: boolean = false
  ): Promise<ApiResponse<DailyBlotter[]>> {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append(`files[${index}]`, file);
    });
    formData.append('process_immediately', processImmediately.toString());
    formData.append('high_priority', highPriority.toString());

    const response = await this.client.post('/blotter/batch-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getProcessingStatus(blotterId: number): Promise<ApiResponse<QueueStatus>> {
    const response = await this.client.get(`/blotter/${blotterId}/status`);
    return response.data;
  }

  async reprocessBlotter(id: number): Promise<ApiResponse<DailyBlotter>> {
    const response = await this.client.post(`/blotter/${id}/reprocess`);
    return response.data;
  }

  async retryBlotter(id: number): Promise<ApiResponse<DailyBlotter>> {
    const response = await this.client.post(`/blotter/${id}/retry`);
    return response.data;
  }

  async cancelBlotter(id: number): Promise<ApiResponse<any>> {
    const response = await this.client.post(`/blotter/${id}/cancel`);
    return response.data;
  }

  async exportBlotter(id: number): Promise<ApiResponse<{ download_url: string }>> {
    const response = await this.client.post(`/blotter/${id}/export`);
    return response.data;
  }

  async deleteBlotter(id: number): Promise<ApiResponse<any>> {
    const response = await this.client.delete(`/blotter/${id}`);
    return response.data;
  }

  // Statistics Methods
  async getBlotterStatistics(): Promise<ApiResponse<BlotterStatistics>> {
    const response = await this.client.get('/blotter/statistics');
    return response.data;
  }

  async getQueueStatistics(): Promise<ApiResponse<QueueStatistics>> {
    const response = await this.client.get('/blotter/queue-stats');
    return response.data;
  }

  // Transaction Methods
  async getTransactions(params?: {
    blotter_id?: number;
    account_string?: string;
    security?: string;
    page?: number;
    per_page?: number;
  }): Promise<PaginatedResponse<Transaction>> {
    const response = await this.client.get('/transactions', { params });
    return response.data;
  }

  async getTransaction(id: number): Promise<ApiResponse<Transaction>> {
    const response = await this.client.get(`/transactions/${id}`);
    return response.data;
  }

  async updateTransaction(id: number, data: Partial<Transaction>): Promise<ApiResponse<Transaction>> {
    const response = await this.client.put(`/transactions/${id}`, data);
    return response.data;
  }

  // User Management Methods
  async getUsers(params?: UserFilters): Promise<PaginatedResponse<User>> {
    const response = await this.client.get('/users', { params });
    return response.data;
  }

  async getUser(id: number): Promise<ApiResponse<User>> {
    const response = await this.client.get(`/users/${id}`);
    return response.data;
  }

  async createUser(data: CreateUserRequest): Promise<ApiResponse<User>> {
    const response = await this.client.post('/users', data);
    return response.data;
  }

  async updateUser(id: number, data: UpdateUserRequest): Promise<ApiResponse<User>> {
    const response = await this.client.put(`/users/${id}`, data);
    return response.data;
  }

  async deleteUser(id: number): Promise<ApiResponse<any>> {
    const response = await this.client.delete(`/users/${id}`);
    return response.data;
  }

  async toggleUserStatus(id: number): Promise<ApiResponse<User>> {
    const response = await this.client.post(`/users/${id}/toggle-status`);
    return response.data;
  }

  async resetUserPassword(id: number): Promise<ApiResponse<{ temporary_password: string }>> {
    const response = await this.client.post(`/users/${id}/reset-password`);
    return response.data;
  }

  // Employee Management Methods
  async getEmployees(params?: {
    page?: number;
    per_page?: number;
    search?: string;
    role?: string;
    is_active?: boolean;
    has_accounts?: boolean;
    account_id?: number;
  }): Promise<any> {
    const response = await this.client.get('/employees', { params });
    return response.data;
  }

  async getEmployeeStats(): Promise<ApiResponse<{
    total_employees: number;
    active_employees: number;
    inactive_employees: number;
    employees_with_assignments: number;
    employees_with_commissions: number;
  }>> {
    const response = await this.client.get('/employees/stats');
    return response.data;
  }

  async getEmployee(id: number): Promise<ApiResponse<User>> {
    const response = await this.client.get(`/employees/${id}`);
    return response.data;
  }

  async getEmployeeCommissionSummary(
    id: number,
    params?: { start_date?: string; end_date?: string }
  ): Promise<ApiResponse<any>> {
    const response = await this.client.get(`/employees/${id}/commission-summary`, { params });
    return response.data;
  }

  async assignEmployeeToAccount(data: {
    user_id: number;
    account_id: number;
    commission_percentage: number;
    is_active?: boolean;
    start_date?: string;
    end_date?: string;
    notes?: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.client.post('/employees/assign-to-account', data);
    return response.data;
  }

  async updateEmployeeAssignment(
    userAccountId: number,
    data: {
      commission_percentage?: number;
      is_active?: boolean;
      start_date?: string;
      end_date?: string;
      notes?: string;
    }
  ): Promise<ApiResponse<any>> {
    const response = await this.client.put(`/employees/assignments/${userAccountId}`, data);
    return response.data;
  }

  async removeEmployeeFromAccount(userAccountId: number, force?: boolean): Promise<ApiResponse<any>> {
    const params = force ? { force: true } : {};
    const response = await this.client.delete(`/employees/assignments/${userAccountId}`, { params });
    return response.data;
  }

  async bulkRemoveEmployeeFromAccounts(userAccountIds: number[], force?: boolean): Promise<ApiResponse<any>> {
    const response = await this.client.post('/employees/bulk-remove-from-accounts', {
      user_account_ids: userAccountIds,
      force: force || false
    });
    return response.data;
  }

  async bulkAssignEmployees(assignments: Array<{
    user_id: number;
    account_id: number;
    commission_percentage: number;
    is_active?: boolean;
    start_date?: string;
    end_date?: string;
    notes?: string;
  }>): Promise<ApiResponse<any>> {
    const response = await this.client.post('/employees/bulk-assign', { assignments });
    return response.data;
  }

  async getAvailableEmployeesForAccount(accountId: number, search?: string): Promise<ApiResponse<User[]>> {
    const params = search ? { search } : {};
    const response = await this.client.get(`/employees/available-for-account/${accountId}`, { params });
    return response.data;
  }

  async getAvailableAccountsForEmployee(employeeId: number, search?: string): Promise<ApiResponse<Account[]>> {
    const params = search ? { search } : {};
    const response = await this.client.get(`/employees/${employeeId}/available-accounts`, { params });
    return response.data;
  }

  async getAccountAllocation(accountId: number): Promise<ApiResponse<any>> {
    const response = await this.client.get(`/employees/accounts/${accountId}/allocation`);
    return response.data;
  }

  async validateCommissionAllocation(data: {
    account_id: number;
    commission_percentage: number;
    exclude_user_account_id?: number;
  }): Promise<ApiResponse<any>> {
    const response = await this.client.post('/employees/validate-commission-allocation', data);
    return response.data;
  }

  async getEmployeePerformanceReport(params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.client.get('/employees/performance-report', { params });
    return response.data;
  }

  // Development/Testing Methods
  async testIntervalBids(): Promise<ApiResponse<any>> {
    const response = await this.client.get('/dev/test-interval-bids');
    return response.data;
  }

  // Commission Adjustment Methods (Move/Split)
  async getCommissionDetails(transactionId: number): Promise<ApiResponse<{
    transaction_id: number;
    gross_commission: number;
    total_user_commission: number;
    net_commission: number;
    commissions: Array<{
      id: number;
      user_id: number;
      user_name: string;
      percentage: number;
      amount: number;
      is_paid: boolean;
    }>;
    can_delete_splits: boolean;
  }>> {
    const response = await this.client.get(`/transactions/${transactionId}/commission/details`);
    return response.data;
  }

  async moveCommission(transactionId: number, data: {
    from_user_id: number;
    to_user_id: number;
    percentage: number;
    permanent?: boolean;
  }): Promise<ApiResponse<any>> {
    const response = await this.client.post(`/transactions/${transactionId}/commission/move`, data);
    return response.data;
  }

  async splitCommission(transactionId: number, data: {
    user_id: number;
    percentage: number;
    permanent?: boolean;
  }): Promise<ApiResponse<any>> {
    const response = await this.client.post(`/transactions/${transactionId}/commission/split`, data);
    return response.data;
  }

  async deleteCommissionSplit(userCommissionId: number): Promise<ApiResponse<any>> {
    const response = await this.client.delete(`/user-commissions/${userCommissionId}/split`);
    return response.data;
  }

  async getActiveEmployeesForCommission(): Promise<ApiResponse<Array<{
    id: number;
    name: string;
    email: string;
  }>>> {
    const response = await this.client.get('/commission-adjustment/employees');
    return response.data;
  }

  // R16 Task A — Manual Check Methods
  async getManualChecks(filters?: ManualCheckListFilters): Promise<ManualCheckListResponse> {
    const response = await this.client.get('/manual-checks', { params: filters });
    return response.data;
  }

  async createManualCheck(payload: CreateManualCheckPayload): Promise<ApiResponse<ManualCheck>> {
    const response = await this.client.post('/manual-checks', payload);
    return response.data;
  }

  async updateManualCheck(id: number, payload: UpdateManualCheckPayload): Promise<ApiResponse<ManualCheck>> {
    const response = await this.client.put(`/manual-checks/${id}`, payload);
    return response.data;
  }

  async deleteManualCheck(id: number): Promise<ApiResponse<any>> {
    const response = await this.client.delete(`/manual-checks/${id}`);
    return response.data;
  }

  async getManualCheckFirms(): Promise<ApiResponse<string[]>> {
    const response = await this.client.get('/manual-checks/firms');
    return response.data;
  }

  /**
   * R17 Task C — distinct dates that carry manual checks in a range.
   * Used by the Blotter Report UI to surface "check-only" days (dates
   * with a check but no CSV blotter) so a salesman's daily PDF is still
   * reachable from the report.
   */
  async getManualCheckDates(
    range: { start_date?: string; end_date?: string }
  ): Promise<ApiResponse<string[]>> {
    const response = await this.client.get('/manual-checks/dates', { params: range });
    return response.data;
  }

  async getManualChecksForSalesman(
    userId: number,
    range?: { start_date?: string; end_date?: string }
  ): Promise<ApiResponse<ManualCheckSalesmanDetail>> {
    const response = await this.client.get(`/manual-checks/salesman/${userId}`, { params: range });
    return response.data;
  }

  // File Download Helper
  async downloadFile(url: string, filename: string): Promise<void> {
    const response = await this.client.get(url, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  }
}

export const apiService = new ApiService();
export default apiService;